import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Icon,
  Tag,
  Stack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  HStack,
  Link,
  Divider,
  Spinner,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
} from "@chakra-ui/react";

import { FiDownload, FiLinkedin, FiMail, FiPhone } from "react-icons/fi";
import { BiMessageAltDetail } from "react-icons/bi";
import { CheckIcon, InfoOutlineIcon, QuestionIcon, TimeIcon } from "@chakra-ui/icons";

import {
  AgencyPassAddActions,
  AgencyRemoveShortlistAction,
  AgencyMarkHiredAction,
  AgencyAddToShortlistAction,
  AgencyInviteAction,
  AgencyFavouriteAction,
  AgencyRecommendAction,
  AgencyChangeStatusMenu,
} from "./AgencyShortlistActions";
import RecommendedNote, {
  RECOMMENDED_ACCENT,
  RecommendedStar,
  isRecommended,
} from "./RecommendedNote";
import { ClientPassAddActions } from "./ClientShortlistActions";
import {
  AGENCY_CANDIDATE_DETAIL_QUERY,
  CLIENT_NOTES_FOR_CANDIDATE_TRIAGE_QUERY,
} from "../../graphql/jobs";
import MoblyzeApolloClient from "../../services/MoblyzeApolloClient";
import { RECRUITER_VIEWED_CANDIDATE_FOR_JOB_MUTATION } from "../../graphql/agencies";
import { LoadingIndicator } from "../LoadingIndicator";
import colors from "../../helpers/colors";
import WithQuery from "../WithQuery";
import { CURRENT_USER_QUERY } from "../../graphql/users";
import { useQuery } from "@apollo/client";
import ProfileImage from "../common/ProfileImage";
import JobInvited from "../common/JobInvited";
import AvailabilityLine, { AvailabilityAge } from "../common/AvailabilityLine";
import BullhornRecordRow from "../common/BullhornRecordRow";
import CandidateSummaryPanel from "../common/CandidateSummaryPanel";
import { bullhornSummaryFor } from "../../helpers/candidateSummary";
import useScreeningConversation from "../../hooks/useScreeningConversation";
import { useBoolFlag, FLAGS } from "../../services/featureFlags";
import MyaReportSection from "./MyaReportSection";
import MyaConversationSection from "./MyaConversationSection";

import ChatDrawer from '../ChatDrawer/ChatDrawer';
import { availabilityLabel } from "../../helpers/availability";

const URL_TAB_TO_INTERNAL = {
  details: "details",
  summary: "cv",
  cv: "cv",
  mya: "ai_screening_chat",
  ai_screening_chat: "ai_screening_chat",
};

const INTERNAL_TAB_TO_URL = {
  details: "details",
  cv: "summary",
  ai_screening_chat: "mya",
};

function ClientNotesInner({ data, loading, error }) {
  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return null;
  }

  const clientNotes = data.clientNotesForCandidateTriage;

  if (!clientNotes || clientNotes.length === 0) {
    return null;
  }

  return (
    <Box>
      <Text fontSize="xl" fontWeight="bold" mb="3">
        Notes from Client:
      </Text>
      <Text fontSize="xl" fontWeight="bold">
        {clientNotes}
      </Text>
    </Box>
  );
}

function ClientNotes({ moblyzeJob, candidate }) {
  if (!candidate || !moblyzeJob) {
    return null;
  }

  return WithQuery(
    ClientNotesInner,
    CLIENT_NOTES_FOR_CANDIDATE_TRIAGE_QUERY,
    { moblyzeJobId: moblyzeJob.id, candidateProfileId: candidate.candidateProfileId },
    {
      moblyzeJob,
      candidate,
    }
  )();
}

function getMatchTier(percentage) {
  if (percentage >= 75) return "high";
  if (percentage >= 50) return "mid";
  return "low";
}

const MATCH_TIER_COLOR = {
  high: "#48BB78",
  mid: "#D69E2E",
  low: "#A0AEC0",
};

// Chip label and tooltip / popover prefix for a claimed (non-definitive)
// match, so the recruiter can tell it apart from a proven one. Keyed by the
// API's criterion category slug; documentable categories get "claimed",
// everything else "unconfirmed".
const CLAIMED_COPY_DEFAULT = {
  label: "Unconfirmed",
  note: "Likely but not confirmed. The candidate will be asked to confirm.",
};
const CLAIMED_COPY_DOCUMENTABLE = {
  label: "Claimed",
  note: "Claimed but not proven. The candidate will be asked to upload documentation.",
};
const CLAIMED_COPY_BY_CATEGORY = {
  right_to_work: CLAIMED_COPY_DOCUMENTABLE,
  certification: CLAIMED_COPY_DOCUMENTABLE,
};

// Shown when a verdict was reached against an older version of the candidate's
// profile — they have since added a CV, a certificate or a skill, and the
// re-check is queued. The verdict is still the best information available, so
// it stays on screen; this says how much to trust it.
//
// Profile edits almost always ADD evidence, so a stale verdict understates the
// candidate far more often than it overstates them. Worth knowing when reading
// one: the real answer is more likely better than shown, not worse.
const STALE_VERDICT_NOTE =
  "Checked before this candidate's latest profile update. Re-checking now.";

function CandidateDetails({
  candidateReport,
  candidateTriage,
  moblyzeJob,
  isClient,
  showAgencyPassAddActions,
  showAgencyRemoveShortlistAction,
  showAgencyMarkHiredAction,
  showAgencyAddToShortlistAction,
  showAgencyInviteAction,
  showAgencyFavouriteAction,
  showAgencyRecommendAction,
  showChangeStatusMenu,
  showClientPassAddActions,
  showClientNotes,
  showAIScreeningChat,
  showRejectedLabel,
  refetch,
  markViewedByRecruiterType,
}) {
  const employmentPreference = null;
  const currentJobTitle = null;
  const visaType = null;
  const currentJobStatus = null;

  // External-candidate rollout flags. While a flag is OFF the matching action
  // is hidden for external (pending sign-up) candidates because the API still
  // rejects it; flip the flag on in the Firebase console to re-enable.
  const externalShortlistEnabled = useBoolFlag(FLAGS.EXTERNAL_CANDIDATE_SHORTLIST);
  const externalInviteEnabled = useBoolFlag(FLAGS.EXTERNAL_CANDIDATE_JOB_INVITE);

  // if recruiter views, call mutation on mount to mark as viewed
  useEffect(() => {
    if (
      !isClient &&
      candidateReport?.viewedByRecruiter === false &&
      markViewedByRecruiterType !== ""
    ) {
      MoblyzeApolloClient.mutate({
        mutation: RECRUITER_VIEWED_CANDIDATE_FOR_JOB_MUTATION,
        variables: {
          candidateProfileId: candidateReport.candidate.candidateProfileId,
          moblyzeJobId: moblyzeJob.id,
          recruiterViewType: markViewedByRecruiterType,
        },
      }).then(() => {
        if (refetch) {
          refetch();
        }
      });
    }
  }, [candidateReport, markViewedByRecruiterType, isClient, moblyzeJob, refetch]);

  const renderActions = ({ candidate, candidateReport, candidateTriage, moblyzeJob }) => {
    // External (pending sign-up) candidates can be viewed/favourited but
    // cannot be moved to shortlist or invited to a job until each rollout flag
    // flips on — the API rejects those actions while the flag is off, so we
    // hide them here too (same pattern as Message/MYA above). The flags gate
    // shortlist and invite independently.
    const isExternal = !!candidate?.isExternal;
    const hideShortlist = isExternal && !externalShortlistEnabled;
    const hideInvite = isExternal && !externalInviteEnabled;

    if (isClient) {
      if (showClientPassAddActions) {
        return (
          <ClientPassAddActions
            candidateTriageStatus={candidateTriage?.status}
            candidate={candidate}
            moblyzeJob={moblyzeJob}
            refetch={refetch}
          />
        );
      }
      return null;
    }

    if (showAgencyPassAddActions) {
      return (
        <AgencyPassAddActions
          candidate={candidate}
          moblyzeJob={moblyzeJob}
          refetch={refetch}
          hideAddToShortlist={hideShortlist}
        />
      );
    }

    if (showAgencyRemoveShortlistAction) {
      return (
        <HStack gap="12px">
          <AgencyRemoveShortlistAction
            candidate={candidate}
            moblyzeJob={moblyzeJob}
            refetch={refetch}
          />
          <ClientPassAddActions
            candidateTriageStatus={candidateTriage?.status}
            candidate={candidate}
            moblyzeJob={moblyzeJob}
            refetch={refetch}
          />
        </HStack>
      );
    }

    if (showAgencyMarkHiredAction) {
      return (
        <AgencyMarkHiredAction
          candidate={candidate}
          moblyzeJob={moblyzeJob}
          refetch={refetch}
        />
      );
    }

    if (showAgencyAddToShortlistAction && !hideShortlist) {
      return (
        <AgencyAddToShortlistAction
          candidate={candidate}
          moblyzeJob={moblyzeJob}
          refetch={refetch}
        />
      );
    }

    if (showAgencyInviteAction && !hideInvite) {
      return (
        <>
          {candidateReport?.invited ?
            <JobInvited invited={candidateReport?.invited}/>
            :
            <AgencyInviteAction
              candidate={candidate}
              moblyzeJob={moblyzeJob}
              refetch={refetch}
            />
          }
        </>
      );
    }

    return null;
  };

  const listCandidate =
    candidateReport?.candidate || candidateTriage?.candidate;

  // Detail data is loaded on demand for the selected candidate so the triage
  // list query stays small. While it's in flight, the tabs show a spinner;
  // the header (name + avatar) renders from the list data immediately.
  const {
    data: detailData,
    loading: detailLoading,
  } = useQuery(AGENCY_CANDIDATE_DETAIL_QUERY, {
    variables: { candidateProfileId: listCandidate?.candidateProfileId },
    skip: !listCandidate?.candidateProfileId,
    fetchPolicy: "cache-and-network",
  });
  const detailedCandidate = detailData?.candidate;
  const candidate = useMemo(() => {
    if (!listCandidate) return detailedCandidate || null;
    if (
      !detailedCandidate ||
      detailedCandidate.candidateProfileId !== listCandidate.candidateProfileId
    ) {
      return listCandidate;
    }
    return { ...listCandidate, ...detailedCandidate };
  }, [listCandidate, detailedCandidate]);
  // Same sentence as the header line, for the details tab. Null when the
  // candidate has never told us (nothing is rendered then).
  const availability = availabilityLabel(candidate?.candidateProfile);
  // Spinner is shown while Apollo is actively loading, or briefly while the
  // selection changes and Apollo still has the previous candidate cached. If
  // the query simply returned no detail, fall back to list data rather than
  // spinning forever.
  const isDetailLoading =
    !!listCandidate?.candidateProfileId &&
    (detailLoading ||
      (!!detailedCandidate?.candidateProfileId &&
        detailedCandidate.candidateProfileId !== listCandidate.candidateProfileId));

  const {
    isOpen: isResumeOpen,
    onOpen: onResumeOpen,
    onClose: onResumeClose,
  } = useDisclosure();

  const [openModalIndex, setOpenModalIndex] = useState(null);

  const openModal = (index) => {
    setOpenModalIndex(index);
  };

  const closeModal = () => {
    setOpenModalIndex(null);
  };

  const { data: user } = useQuery(CURRENT_USER_QUERY);

  const bullhornSummary = bullhornSummaryFor(candidate?.candidateProfile);

  const [searchParams, setSearchParams] = useSearchParams();

  const [detailActiveTab, setDetailActiveTab] = useState(() => {
    const urlTab = searchParams.get("tab");
    return URL_TAB_TO_INTERNAL[urlTab] || "details";
  });

  // Fetch screening conversation data only when MYA tab is active and visible.
  // AI screening is inherently user-keyed (the conversation store keys on the
  // candidate's user id), so feed it candidate.userId — not the profile id.
  // candidate.userId is null for pending sign-ups, so the fetch is disabled
  // for them along with the tab below.
  const { data: screeningData, loading: screeningLoading, error: screeningError } = useScreeningConversation(
    moblyzeJob?.id,
    candidate?.userId,
    detailActiveTab === "ai_screening_chat" && showAIScreeningChat && !candidate?.isExternal
  );

  const criteriaCategories = moblyzeJob?.criteriaCategories || [];
  const matchingSource = candidateReport || candidateTriage;
  const matchingCriteriaCategories = matchingSource?.matchingCriteriaCategories || [];
  const isMatchingInProgress = matchingSource?.matchingStatus === "Started";
  // Any verdict predating the candidate's latest profile change. Derived from
  // the per-criterion flags rather than a separate field so the summary line
  // and the individual tags can never disagree.
  const hasStaleVerdicts = matchingCriteriaCategories.some((cat) =>
    (cat.requirements || []).some((r) => r.isStale)
  );

  const { isOpen: isChatDrawerOpen, onOpen: onChatDrawerOpen, onClose: onChatDrawerClose } = useMemo(
    () => ({
      isOpen: searchParams.get("chat") === "open",
      onOpen: () =>
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("chat", "open");
            return next;
          },
          { replace: true }
        ),
      onClose: () =>
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("chat");
            return next;
          },
          { replace: true }
        ),
    }),
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    const intercomWidgets = document.querySelectorAll('.intercom-launcher-frame, .intercom-launcher, .intercom-messenger-frame, .intercom-with-namespace-52k34s');

    intercomWidgets.forEach((widget) => {
      if (widget instanceof HTMLElement) {
        widget.style.right = isChatDrawerOpen ? '520px' : '20px';
      }
    });
  }, [isChatDrawerOpen]);

  const handleTabClick = useCallback(
    (tab) => {
      setDetailActiveTab(tab);
      const urlValue = INTERNAL_TAB_TO_URL[tab];
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (urlValue) {
            next.set("tab", urlValue);
          } else {
            next.delete("tab");
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // The MYA (AI screening) tab is user-bound and hidden for external
  // candidates. A direct deep-link (?tab=mya) can still land an external
  // candidate on that tab, where the button is hidden and the panel no longer
  // renders — leaving a blank "invisible tab". Force them back to Details.
  useEffect(() => {
    if (candidate?.isExternal && detailActiveTab === "ai_screening_chat") {
      handleTabClick("details");
    }
  }, [candidate?.isExternal, detailActiveTab, handleTabClick]);

  return (
    <Flex
      direction="column"
      flex={1}
      gap="24px"
      overflowY="auto"
    >
      {showClientNotes && (
        <ClientNotes candidate={candidate} moblyzeJob={moblyzeJob} />
      )}

      <Stack gap="8px">
        <Stack gap="15px" bgColor={colors.black} p="16px"
          borderRadius="12px"
          backgroundColor={colors.black}
          borderLeft={isRecommended(candidateReport) ? "3px solid" : undefined}
          borderLeftColor={isRecommended(candidateReport) ? RECOMMENDED_ACCENT : undefined}
        >
          <Flex alignItems="center">
            <ProfileImage
              imageUrl={candidate?.profileImageUrl}
              name={candidate?.fullName}
              size="65px"
            />
            <Stack ml={"24px"}>
              <Flex gap="16px" alignItems="center">
                <HStack spacing="8px" data-testid="candidate-name">
                  <Text fontSize="xl" fontWeight="bold">
                    {candidate?.fullName}
                  </Text>
                  {!isClient && isRecommended(candidateReport) && (
                    <RecommendedStar boxSize="16px" />
                  )}
                </HStack>
                {showRejectedLabel && (
                  <Tag
                    colorScheme="red"
                    variant="subtle"
                    textTransform="uppercase"
                    fontSize="11px"
                  >
                    Rejected
                  </Tag>
                )}
              </Flex>
              <HStack>
                {candidate?.isExternal ? (
                  <Tag
                    backgroundColor="#ECC94B"
                    textTransform="uppercase"
                    color={colors.black}
                    fontSize={"11px"}
                    variant="solid"
                    h="15px"
                  >
                    External
                  </Tag>
                ) : null}
                {candidateReport?.isInternalTestUser && (
                  <Tag
                    colorScheme="purple"
                    variant="subtle"
                    textTransform="uppercase"
                    fontSize="11px"
                    h="15px"
                  >
                    Test account
                  </Tag>
                )}
              </HStack>
              <AvailabilityLine profile={candidate?.candidateProfile} />
            </Stack>
          </Flex>
          {!isClient && (
            <RecommendedNote candidateReport={candidateReport} fontSize="sm" />
          )}
          <HStack gap="16px" flexWrap="wrap">
            {/* Recommend / Edit reason / Remove recommendation. Not gated on
                the external-candidate flags: the API allows recommending
                external candidates. */}
            {!isClient && showAgencyRecommendAction && (
              <AgencyRecommendAction
                candidateReport={candidateReport}
                moblyzeJob={moblyzeJob}
                refetch={refetch}
              />
            )}
            {renderActions({ candidate, candidateReport, candidateTriage, moblyzeJob })}
            {showAgencyFavouriteAction && (<AgencyFavouriteAction
              candidateReport={candidateReport}
              refetch={refetch}
            />)}
            {!candidate?.isExternal && (
              <Button
                variant="outline"
                borderColor="white"
                _hover={{ borderColor: "white", bg: "transparent" }}
                color="white"
                onClick={() => {
                  onChatDrawerOpen();
                }}
              >
                <Icon as={BiMessageAltDetail} mr="6px" />
                Message
              </Button>
            )}
            {/* "Change status" is a shortlist shortcut ("Skip to
                Shortlisted"), so it's gated on the shortlist flag for external
                candidates for the same reason as the shortlist action. */}
            {showChangeStatusMenu &&
              !(candidate?.isExternal && !externalShortlistEnabled) && (
              <AgencyChangeStatusMenu
                candidate={candidate}
                moblyzeJob={moblyzeJob}
                refetch={refetch}
              />
            )}
          </HStack>
        </Stack>

        {candidate?.isExternal && (
          <HStack
            gap="8px"
            align="center"
            px="12px"
            py="8px"
            borderRadius="8px"
            bg="whiteAlpha.100"
          >
            <Tooltip
              label="This candidate has not installed the Moblyze app yet. Reach out and have them apply on Moblyze"
              hasArrow
              placement="top"
            >
              <span style={{ display: "inline-flex" }}>
                <InfoOutlineIcon color="yellow.300" boxSize="16px" />
              </span>
            </Tooltip>
            <Text fontSize="sm" color="whiteAlpha.800">
              This candidate has not installed the Moblyze app yet. Reach out and have them apply on Moblyze.
            </Text>
          </HStack>
        )}

        <Stack gap="15px" bgColor={colors.black} p="16px"
          borderRadius="12px"
          backgroundColor={colors.black}
        >
          <HStack>
            <Button
              onClick={() => handleTabClick("details")}
              backgroundColor={detailActiveTab === "details" ? "whiteAlpha.300" : "transparent"}
              color={detailActiveTab === "details" ? "white" : "whiteAlpha.800"}
              fontWeight={detailActiveTab === "details" ? "bold" : "normal"}
              fontSize="16px"
            >
              Details
            </Button>
            <Button
              onClick={() => handleTabClick("cv")}
              backgroundColor={detailActiveTab === "cv" ? "whiteAlpha.300" : "transparent"}
              color={detailActiveTab === "cv" ? "white" : "whiteAlpha.800"}
              fontWeight={detailActiveTab === "cv" ? "bold" : "normal"}
              fontSize="16px"
            >
              CV/Resume
            </Button>
            {showAIScreeningChat && !candidate?.isExternal && (<Button
              onClick={() => handleTabClick("ai_screening_chat")}
              backgroundColor={detailActiveTab === "ai_screening_chat" ? "whiteAlpha.300" : "transparent"}
              color={detailActiveTab === "ai_screening_chat" ? "white" : "whiteAlpha.800"}
              fontWeight={detailActiveTab === "ai_screening_chat" ? "bold" : "normal"}
              fontSize="16px"
            >
              MYA
            </Button>)}
          </HStack>
          <Divider />

          {detailActiveTab === "details" && (
            <Stack gap="24px">
              {isMatchingInProgress ? (
                <HStack gap="12px" py="16px">
                  <Spinner size="sm" color="teal.300" />
                  <Text fontSize="md" color="whiteAlpha.800">
                    Matching candidate against job requirements...
                  </Text>
                </HStack>
              ) : (
                <>
                {hasStaleVerdicts && (
                  <HStack
                    gap="8px"
                    py="8px"
                    data-testid="stale-verdicts-banner"
                    color="whiteAlpha.700"
                  >
                    <Icon as={TimeIcon} boxSize="12px" />
                    <Text fontSize="sm">
                      This candidate updated their profile after some checks
                      were made. Those are marked and being re-checked now.
                    </Text>
                  </HStack>
                )}
                {criteriaCategories.map((category) => {
                  const matchingCategory = matchingCriteriaCategories.find(
                    (mt) => mt.slug === category.slug
                  );
                  const totalReqs = category.criteria.length;
                  const matchingCount = matchingCategory
                    ? matchingCategory.requirements.filter((r) => r.matches).length
                    : 0;
                  const matchPercentage = totalReqs > 0
                    ? Math.round((matchingCount / totalReqs) * 100)
                    : 0;
                  const tier = getMatchTier(matchPercentage);
                  const tierColor = MATCH_TIER_COLOR[tier];

                  return (
                    <Stack key={category.slug} gap="12px">
                      <Text fontSize="xl" fontWeight="bold">
                        {category.name}{" "}
                        <Text as="span" fontWeight="normal" color={tierColor}>
                          ({matchingCount} of {totalReqs} matched)
                        </Text>
                      </Text>
                      <Flex wrap="wrap" gap="8px">
                        {category.criteria.map((req) => {
                          const matchingReq = matchingCategory?.requirements.find(
                            (mr) => mr.criterionId === req.id
                          );
                          const matches = matchingReq?.matches ?? false;
                          // The classifier's soft "yes": claimed / plausible
                          // but not evidenced. Rows predating the flag are
                          // treated as definitive so they keep rendering green.
                          const isClaimed =
                            matches && matchingReq?.isDefinitive === false;
                          const isMissingRequired = !matches && req.isRequired;
                          const isMissingOptional = !matches && !req.isRequired;

                          let backgroundColor = "whiteAlpha.100";
                          let textColor = "whiteAlpha.500";
                          if (isClaimed) {
                            backgroundColor = "#ECC94B";
                            textColor = "#1A202C";
                          } else if (matches) {
                            backgroundColor = "#48BB78";
                            textColor = "#ffffff";
                          } else if (isMissingRequired) {
                            backgroundColor = "#4A5568";
                            textColor = "whiteAlpha.900";
                          }

                          const claimedCopy = isClaimed
                            ? CLAIMED_COPY_BY_CATEGORY[category.slug] ?? CLAIMED_COPY_DEFAULT
                            : null;
                          // Staleness is orthogonal to the verdict: a matched
                          // and a missing criterion can each be behind the
                          // profile. So it does not touch the tag's colour,
                          // which encodes the verdict — it dims the tag and
                          // adds an icon and a note instead.
                          const isStale = matchingReq?.isStale === true;
                          const reasonText = [
                            claimedCopy?.note,
                            matchingReq?.reason,
                            isStale ? STALE_VERDICT_NOTE : null,
                          ]
                            .filter(Boolean)
                            .join(" ");
                          const hasReason = reasonText.length > 0;
                          return (
                            <Tooltip
                              key={req.id}
                              label={reasonText}
                              isDisabled={!hasReason}
                              hasArrow
                              placement="top"
                            >
                              <Tag
                                variant="solid"
                                data-testid={`criterion-tag-${req.id}`}
                                data-match-state={
                                  isClaimed ? "claimed" : matches ? "matched" : "missing"
                                }
                                data-stale={isStale ? "true" : undefined}
                                backgroundColor={backgroundColor}
                                color={textColor}
                                opacity={isStale ? 0.65 : 1}
                                fontSize="14px"
                                fontWeight="500"
                                padding="8px 10px"
                              >
                                {isClaimed ? (
                                  <Icon as={QuestionIcon} boxSize="12px" mr="6px" />
                                ) : matches ? (
                                  <Icon as={CheckIcon} boxSize="10px" mr="6px" />
                                ) : (
                                  <Text as="span" mr="6px" fontWeight="bold">
                                    —
                                  </Text>
                                )}
                                {req.title}
                                {isStale && (
                                  <Icon
                                    as={TimeIcon}
                                    boxSize="10px"
                                    ml="6px"
                                    aria-label="Checked before the latest profile update"
                                  />
                                )}
                                {(isClaimed || isMissingRequired || isMissingOptional) && (
                                  <Text
                                    as="span"
                                    ml="8px"
                                    fontSize="xs"
                                    textTransform="uppercase"
                                    letterSpacing="wider"
                                    opacity={0.85}
                                  >
                                    {isClaimed ? claimedCopy.label : "Missing"}
                                  </Text>
                                )}
                                {hasReason && (
                                  <Popover trigger="click" placement="top" isLazy>
                                    <PopoverTrigger>
                                      <Box
                                        as="button"
                                        type="button"
                                        display="inline-flex"
                                        alignItems="center"
                                        ml="8px"
                                        opacity={0.8}
                                        _hover={{ opacity: 1 }}
                                        onClick={(e) => e.stopPropagation()}
                                        aria-label="Show reason"
                                      >
                                        <InfoOutlineIcon boxSize="12px" />
                                      </Box>
                                    </PopoverTrigger>
                                    <PopoverContent bg="gray.700" color="white" border="none" maxW="320px">
                                      <PopoverArrow bg="gray.700" />
                                      <PopoverBody fontSize="sm">{reasonText}</PopoverBody>
                                    </PopoverContent>
                                  </Popover>
                                )}
                              </Tag>
                            </Tooltip>
                          );
                        })}
                      </Flex>
                    </Stack>
                  );
                })}
                </>
              )}

              {isDetailLoading ? (
                <LoadingIndicator />
              ) : (
                <>
                  <Stack gap="12px">
                    <Text fontSize="xl" fontWeight="bold">Contact</Text>
                    <Stack>
                      <HStack>
                        <Icon as={FiMail} />
                        <Link color={"white"} href={"mailto:" + candidate?.email}>{candidate?.email}</Link>
                      </HStack>
                      <HStack>
                        <Icon as={FiPhone} />
                        <Link color={"white"} href={"tel:" + candidate?.primaryPhone}>{candidate?.primaryPhone}</Link>
                      </HStack>
                      {candidate?.candidateProfile?.linkedInURL && (
                        <HStack>
                          <Icon as={FiLinkedin} />
                          <Link color={"white"} target="_blank" href={candidate?.candidateProfile?.linkedInURL}>{candidate?.candidateProfile?.linkedInURL}</Link>
                        </HStack>
                      )}
                    </Stack>
                  </Stack>

                  <Stack gap="12px">
                    <Text fontSize="xl" fontWeight="bold">
                      Referrals:&nbsp;
                      {candidate?.referrals?.length ? `(${candidate.referrals.length} referrals)` : "None yet"}
                    </Text>
                    <Stack>
                      <HStack mb={1}>
                        <Text>Code:</Text>
                        <Text>{candidate?.referralCode}</Text>
                      </HStack>
                    </Stack>
                  </Stack>

                  {candidate?.candidateProfile?.agencySlugId && (
                    <Flex alignItems="center" gap="4px" mt="16px">
                      <img
                        src={candidate?.candidateProfile?.agencyLogoUrl}
                        height={20}
                        alt={candidate?.candidateProfile?.agencySlugId}
                        // Height-led, so a wide wordmark keeps its shape instead of being
                        // squeezed into a 20px square.
                        style={{
                          height: "20px",
                          width: "auto",
                          maxWidth: "80px",
                          objectFit: "contain",
                          paddingRight: "4px",
                        }}
                      />
                      <Text
                        fontSize="md"
                        fontWeight="normal"
                        lineHeight="6"
                        color={colors.greyLight}
                      >
                        {candidate?.candidateProfile?.agencySlugId}
                      </Text>
                    </Flex>
                  )}

                  <BullhornRecordRow
                    bullhornId={candidate?.candidateProfile?.bullhornId}
                    bullhornUrl={candidate?.candidateProfile?.bullhornUrl}
                    mt="16px"
                  />
                </>
              )}

              {!!visaType && (
                <Flex direction="column" flex={1} gap="12px">
                  <Text fontSize="xl" fontWeight="bold">
                    Visa Type
                  </Text>
                  <Text fontSize="md" fontWeight="normal" lineHeight="6">
                    {visaType}
                  </Text>
                </Flex>
              )}

              {currentJobStatus && (
                <Flex paddingTop="24px" gap="24px">
                  <Flex direction="column" gap="12px">
                    <Text fontSize="xl" fontWeight="bold">
                      Current Job Status
                    </Text>
                    <Text fontSize="md" fontWeight="normal" lineHeight="6">
                      {currentJobStatus}
                    </Text>
                  </Flex>
                </Flex>
              )}

              {!!currentJobTitle && (
                <Flex direction="column" flex={1} gap="12px">
                  <Text fontSize="md" fontWeight="semibold" lineHeight="6">
                    Current Job Title
                  </Text>
                  <Text fontSize="md" fontWeight="normal" lineHeight="6">
                    {currentJobTitle}
                  </Text>
                </Flex>
              )}

              {availability && (
                <Flex direction="column" flex={1} gap="12px">
                  <Text fontSize="md" fontWeight="semibold" lineHeight="6">
                    Availability
                  </Text>
                  <Text fontSize="md" fontWeight="normal" lineHeight="6">
                    {availability.text}
                    {availability.age && " "}
                    {availability.age && (
                      <AvailabilityAge age={availability.age} fontSize="xs" />
                    )}
                  </Text>
                </Flex>
              )}
              {employmentPreference && (
                <Flex direction="column" flex={1} gap="12px">
                  <Text fontSize="md" fontWeight="semibold" lineHeight="6">
                    Employee Preference
                  </Text>
                  <Text fontSize="md" fontWeight="normal" lineHeight="6">
                    {employmentPreference}
                  </Text>
                </Flex>
              )}

            </Stack>)}
          {detailActiveTab === "cv" && (
            <Stack gap="24px">
              {isDetailLoading && <LoadingIndicator />}
              {/* Sits outside the resumeUrl gate below: an imported candidate
                  often has a Bullhorn blurb and no CV file at all. */}
              {!isDetailLoading && (
                <CandidateSummaryPanel
                  title="Bullhorn Summary"
                  text={bullhornSummary}
                  testId="bullhorn-summary"
                />
              )}
              {!isDetailLoading && candidate?.candidateProfile?.resumeUrl && (
                <>
                  <Box>
                    <Text fontSize="xl" fontWeight="bold" mb="4">
                      CV/Resume
                    </Text>

                    {candidate?.candidateProfile?.resumeSummary ? (
                      <CandidateSummaryPanel
                        title="AI summary"
                        html={candidate?.candidateProfile?.resumeSummary}
                        testId="ai-summary"
                      />
                    ) : (
                      <>
                        <Flex
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                          mb="4"
                        >
                          <Text fontSize="md" fontWeight="semibold" lineHeight="6">
                          </Text>

                          <a
                            href={candidate?.candidateProfile?.resumeUrl}
                            download
                            style={{ textDecoration: "none" }}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Flex
                              alignItems="center"
                              justifyContent="center"
                              borderRadius="20px"
                              p="4px 8px"
                              fontSize="sm"
                              fontWeight="semibold"
                              lineHeight="6"
                              color="white"
                              cursor="pointer"
                            >
                              <Icon color="white" fontSize="16px" as={FiDownload} mr="2" />
                              Download
                            </Flex>
                          </a>
                        </Flex>

                        <iframe
                          title="resume"
                          width="100%"
                          height="800px"
                          src={candidate?.candidateProfile?.resumePreviewUrl}
                        ></iframe>
                      </>
                    )}
                  </Box>

                  {candidate?.candidateProfile?.resumeSummary && (
                    <Box>
                      <Button onClick={onResumeOpen} variant="outline">
                        Open CV
                      </Button>
                      <Modal
                        isOpen={isResumeOpen}
                        onClose={onResumeClose}
                        isCentered
                        size={"4xl"}
                      >
                        <ModalOverlay />
                        <ModalContent>
                          <ModalHeader color={colors.black}>
                            <Flex
                              flexDirection={"row"}
                              alignItems={"center"}
                              justifyContent={"space-between"}
                            >
                              <Text>{candidate?.fullName}</Text>
                              <a
                                href={candidate?.candidateProfile?.resumeUrl}
                                download
                                style={{
                                  textDecoration: "none",
                                  marginRight: "40px",
                                  fontSize: "14px",
                                  alignItems: "center",
                                  display: "flex",
                                  position: "relative",
                                  top: "-4px",
                                }}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <Icon
                                  color={colors.black}
                                  fontSize="16px"
                                  as={FiDownload}
                                  mr="2"
                                />
                                Download
                              </a>
                            </Flex>
                          </ModalHeader>
                          <ModalCloseButton />
                          <ModalBody>
                            <iframe
                              title="resume"
                              width="100%"
                              height="700px"
                              src={candidate?.candidateProfile?.resumePreviewUrl}
                            />
                          </ModalBody>
                          <ModalFooter>
                            <Button colorScheme="blue" mr={3} onClick={onResumeClose}>
                              Close
                            </Button>
                          </ModalFooter>
                        </ModalContent>
                      </Modal>
                    </Box>
                  )}
                </>
              )}

              {/* Certifications */}
              {!isDetailLoading && (
              <Box>
                {candidate?.candidateProfile?.certification?.length > 0 && (
                  <Text fontSize="xl" fontWeight="bold" mb="2">
                    Certifications
                  </Text>
                )}
                {candidate?.candidateProfile?.certification?.map((cert, index) => (
                  <Box key={index} mt="12px">
                    <Text fontSize="sm" fontWeight="normal" mb="2">
                      {cert.name}
                    </Text>
                    <Button
                      onClick={() => openModal(index)}
                      variant="outline"
                      colorScheme=""
                    >
                      Open {cert.name}
                    </Button>
                    <Modal
                      isOpen={openModalIndex === index}
                      onClose={closeModal}
                      isCentered
                      size={"4xl"}
                    >
                      <ModalOverlay />
                      <ModalContent>
                        <ModalHeader color={colors.black}>
                          <Flex
                            flexDirection={"row"}
                            alignItems={"center"}
                            justifyContent={"space-between"}
                          >
                            <Text color={colors.black}>{cert?.name}</Text>
                            <a
                              href={cert?.url}
                              download
                              style={{
                                textDecoration: "none",
                                marginRight: "40px",
                                fontSize: "14px",
                                alignItems: "center",
                                display: "flex",
                                position: "relative",
                                top: "-4px",
                              }}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <Icon
                                color={colors.black}
                                fontSize="16px"
                                as={FiDownload}
                                mr="2"
                              />
                              Download
                            </a>
                          </Flex>
                        </ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                          <iframe
                            title="certification"
                            width="100%"
                            height="700px"
                            src={cert?.url}
                          />
                        </ModalBody>
                        <ModalFooter>
                          <Button colorScheme="blue" mr={3} onClick={closeModal}>
                            Close
                          </Button>
                        </ModalFooter>
                      </ModalContent>
                    </Modal>
                  </Box>
                ))}
              </Box>
              )}
            </Stack>)}
          {showAIScreeningChat && !candidate?.isExternal && detailActiveTab === "ai_screening_chat" && (
            <Stack gap="16px">
              {isDetailLoading ? (
                <LoadingIndicator />
              ) : screeningLoading ? (
                <LoadingIndicator />
              ) : screeningError ? (
                <Box
                  backgroundColor={colors.black}
                  borderRadius="12px"
                  p="24px"
                >
                  <Text fontSize="xl" fontWeight="bold" color="white" mb="8px">
                    Unable to load screening chat
                  </Text>
                  <Text fontSize="sm" color="gray.400">
                    {screeningError}
                  </Text>
                </Box>
              ) : !screeningData?.conversation ? (
                <Box
                  backgroundColor={colors.black}
                  borderRadius="12px"
                  p="24px"
                  textAlign="center"
                >
                  <Text fontSize="xl" fontWeight="bold" color="white" mb="8px">
                    No MYA Screening Available
                  </Text>
                  <Text fontSize="sm" color="gray.400">
                    This candidate hasn't completed an AI screening conversation yet.
                  </Text>
                </Box>
              ) : (
                <Stack gap="6px">
                  {/* MYA Report Section */}
                  <MyaReportSection
                    evaluation={screeningData.conversation.evaluation}
                    messages={screeningData.conversation.messages}
                    candidate={candidate}
                    parsedMetadata={screeningData.conversation.conversation?.parsedMetadata}
                  />

                  {/* MYA Conversation Section */}
                  <MyaConversationSection
                    messages={screeningData.conversation.messages}
                    candidate={candidate}
                    parsedMetadata={screeningData.conversation.conversation?.parsedMetadata}
                  />
                </Stack>
              )}
            </Stack>)}
        </Stack>
        <ChatDrawer
          isOpen={isChatDrawerOpen}
          onClose={onChatDrawerClose}
          currentUser={user?.currentUser}
          candidate={candidate}
        />
      </Stack>
    </Flex>
  );
}

export default CandidateDetails;
